import React, { useState, useEffect, useCallback } from "react";
import TeacherHeader from "../common/TeacherHeader";
import Footer from "../common/Footer";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { baseUrl } from "../../Utility/constants";
import { getSchoolId, fetchSubjectGroupMaster } from "../../Utility/commonFunctions";
import { MarksFeedingEndpoints } from "../../Utility/Endpoints";

const ViewMarksFeeding = () => {
  const [schoolId, setSchoolId] = useState(null);
  const [standards, setStandards] = useState([]);
  const [sections, setSections] = useState([]);
  const [streams, setStreams] = useState([]);
  const [subjectGroups, setSubjectGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [students, setStudents] = useState([]);
  const [marksFeedingId, setMarksFeedingId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [marksData, setMarksData] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isMarkFeeded, setIsMarkFeeded] = useState(false);

  const [selected, setSelected] = useState({
    standardId: "",
    sectionId: "",
    streamId: "",
    subjectGroupId: "",
    subjectId: "",
    examType: "",
    marksFeedingExamType: "",
    maxMarks: 0,
  });

  // -------------------- Helpers --------------------

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelected((prev) => ({ ...prev, [name]: value }));

    if (name === "subjectId") {
      setExamTypes([]);
      setStudents([]);
    }
    if (name === "examType" && value === "") {
      setStudents([]);
    }
  };

  const fetchMarks = useCallback(async () => {
    const { standardId, sectionId, streamId, subjectGroupId, subjectId, examType } = selected;
    if (!standardId || !sectionId || !streamId || !subjectGroupId || !subjectId || !examType) return;

    try {
      const url = `${baseUrl}/api/MarksFeeding/StudentDetailsByStandardIdAndSectionId?StandardId=${standardId}&SectionId=${sectionId}&streamId=${streamId}&subjectGroup=${subjectGroupId}&subjectId=${subjectId}&examType=${examType}`;
      const { data } = await axios.get(url);

      if (!data || data.length === 0) {
        setStudents([]);
        return;
      }

      const isMarked = data.some((stu) => stu.markStatus === "Marked");
      setIsMarkFeeded(isMarked);

      if (!isMarked) {
        toast.warning("No marks have been submitted for this exam type.");
        setStudents([]);
        return;
      }

      if (examType === "Final") {
        // Merge final marks per student
        const merged = Object.values(
          data.reduce((acc, stu) => {
            const id = stu.studentId;
            if (!acc[id]) {
              acc[id] = {
                ...stu,
                finalMarks: {},
                marksFeedingIds: {},
                examMaxMarks: stu.examMaxMarks || {},
              };
            }

            // Merge finalMarks correctly
            if (stu.finalMarks && Object.keys(stu.finalMarks).length > 0) {
              Object.entries(stu.finalMarks).forEach(([key, val]) => {
                acc[id].finalMarks[key] = {
                  marks: val?.marks ?? 0,
                  marksFeedingId: val?.marksFeedingId ?? null,
                };
                acc[id].marksFeedingIds[key] = val?.marksFeedingId ?? null;
                acc[id].examMaxMarks[key] = val?.maxMarks ?? 0; // <-- important
              });
            } else if (stu.examMarkType) {
              acc[id].finalMarks[stu.examMarkType] = {
                marks: stu.marksObtained ?? 0,
                marksFeedingId: stu.marksFeedingId ?? null,
              };
              acc[id].marksFeedingIds[stu.examMarkType] = stu.marksFeedingId ?? null;
              acc[id].examMaxMarks[stu.examMarkType] = stu.maxMarks ?? 0; // <-- map maxMarks
            }

            // Other info
            acc[id].rollNo = stu.rollNo;
            acc[id].generalRegisterNo = stu.generalRegisterNo;
            acc[id].firstName = stu.firstName;
            acc[id].middleName = stu.middleName;
            acc[id].lastName = stu.lastName;
            acc[id].isEditAllowedIsEditMarks = stu.isEditAllowedIsEditMarks;

            return acc;
          }, {})
        ).sort((a, b) => (a.examSeatNo || 0) - (b.examSeatNo || 0));;

        setStudents(merged);
      } else {
        setStudents(data);
      }
    } catch (err) {
      console.error("Error fetching marks:", err);
      setStudents([]);
    }
  }, [selected]);

  const handleRaiseEditMarksRequest = async (marksFeedingId) => {
    if (!marksFeedingId) {
      toast.error("Invalid marks feeding ID");
      return;
    }

    try {
      const url = `${MarksFeedingEndpoints.RaiseEditMarksRequest}?id=${marksFeedingId}`;
      const res = await axios.put(url);

      if (res.status === 200 && res.data) {
        toast.success(res.data?.message);
        fetchMarks();
      } else {
        toast.error("Failed to raise the request.");
      }
    } catch {
      toast.error("Failed to raise the request.");
    }
  };

  const handleUpdateMarks = (student) => {
    if (selected.examType !== "Final") {
      if (!student?.marksFeedingId) {
        toast.error("Invalid marks feeding ID");
        return;
      }
      const enrichedStudent = {
        ...student,
        marksFeedingExamType: selected.examType,
        maxMarks: student.maxMarks ?? 0,
      };

      setMarksFeedingId(student.marksFeedingId);
      setSelectedStudent(enrichedStudent);
      setMarksData({ [selected.examType]: student.marksObtained ?? 0 });
      setIsModalOpen(true);
    } else {
      if (!student?.marksFeedingIds || Object.keys(student.marksFeedingIds).length === 0) {
        toast.error("Invalid marks feeding IDs for final categories");
        return;
      }

      const initMarksData = {};
      Object.keys(student.finalMarks || {}).forEach((key) => {
        initMarksData[key] = student.finalMarks[key]?.marks ?? 0;
      });

      setMarksFeedingId(student.marksFeedingIds);
      setSelectedStudent(student);
      setMarksData(initMarksData);
      setIsModalOpen(true);
    }
  };

  const handleMarksChange = (field, value, max) => {

    if (value === "") {
      setMarksData((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    const numeric = Number(value);
    if (Number.isNaN(numeric)) return;

    if (max !== undefined && numeric > max) {
      toast.error(`Marks for ${field} cannot exceed ${max}`);
      return;
    }

    setMarksData((prev) => ({ ...prev, [field]: numeric }));
  };

  const handleSubmitMarks = async () => {
    try {
      if (selected.examType !== "Final") {
        const payloadMarks = marksData[selected.examType] ?? 0;

        const res = await axios.put(
          `${MarksFeedingEndpoints.UpdateMarksRequest}?id=${marksFeedingId}&marksObtained=${payloadMarks}`
        );

        if (res.status === 200 && res.data) {
          toast.success(res.data?.message);
          await fetchMarks();
        } else {
          toast.error("Failed to update marks.");
        }
      } else {
        const map = marksFeedingId;
        if (!map || typeof map !== "object") {
          toast.error("No IDs found to update final categories.");
          setIsModalOpen(false);
          setMarksData({});
          return;
        }

        const updates = Object.keys(map).map((catType) => {
          const idForCat = map[catType];
          const marksToSend = marksData[catType] ?? selectedStudent.finalMarks?.[catType]?.marks ?? 0;
          return axios.put(`${MarksFeedingEndpoints.UpdateMarksRequest}?id=${idForCat}&marksObtained=${marksToSend}`);
        });

        try {
          const results = await Promise.all(updates);
          const allSuccess = results.every((r) => r.status === 200);
          toast.success(allSuccess ? "Final marks updated successfully." : "Final marks updated (some updates may have failed).");
          await fetchMarks();
        } catch (err) {
          console.error("Error updating final marks:", err);
          toast.error("Failed to update final marks.");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmitMarks:", error);
      toast.error("Failed to update marks");
    }

    setIsModalOpen(false);
    setMarksData({});
    setMarksFeedingId("");
    setSelectedStudent(null);
  };

  // -------------------- Effects --------------------

  useEffect(() => {
    (async () => {
      const id = await getSchoolId();
      setSchoolId(id);

      const groups = await fetchSubjectGroupMaster();
      setSubjectGroups(groups);

      const { data: streamData } = await axios.get(`${baseUrl}/api/StreamMaster`);
      setStreams(streamData);
    })();
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${baseUrl}/api/StandardMaster/GetStandardBySchool?id=${schoolId}`);
        setStandards(data);
      } catch (err) {
        console.error("Error fetching standards:", err);
      }
    })();
  }, [schoolId]);

  useEffect(() => {
    if (!selected.standardId) {
      setSections([]);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${baseUrl}/api/SectionMaster/GetSectionByStandardID?id=${selected.standardId}`);
        setSections(data);
      } catch (err) {
        console.error("Error fetching sections:", err);
      }
    })();
  }, [selected.standardId]);

  useEffect(() => {
    const { standardId, streamId, subjectGroupId } = selected;
    if (!standardId || !streamId || !subjectGroupId) {
      setSubjects([]);
      return;
    }

    (async () => {
      try {
        const teacherId = localStorage.getItem("userID");
        const url = `${baseUrl}/api/SubjectMaster/GetSubjectMasterByClassAndStream?classId=${standardId}&streamId=${streamId}&subjectGroup=${subjectGroupId}&teacherId=${teacherId}`;
        const { data } = await axios.get(url);
        setSubjects(data);
      } catch (err) {
        if (err.response?.status === 404) setSubjects([]);
        else console.error("Error fetching subjects:", err);
      }
    })();
  }, [selected.standardId, selected.streamId, selected.subjectGroupId]);

  useEffect(() => {
    if (!selected.subjectId) {
      setExamTypes([]);
      setStudents([]);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${baseUrl}/api/CommonMaster/GetExamTypes?subjectId=${selected.subjectId}`);
        setExamTypes(data);
      } catch {
        setExamTypes([]);
        setStudents([]);
      }
    })();
  }, [selected.subjectId]);

  useEffect(() => {
    fetchMarks();
  }, [fetchMarks]);

  // -------------------- Render --------------------

  const dropdowns = [
    { label: "Standard", name: "standardId", options: standards, labelKey: "standardName", valueKey: "id" },
    { label: "Section", name: "sectionId", options: sections, labelKey: "name", valueKey: "id" },
    { label: "Stream", name: "streamId", options: streams, labelKey: "name", valueKey: "id" },
    { label: "Group", name: "subjectGroupId", options: subjectGroups, labelKey: "subjectGroupName", valueKey: "subjectGroupId" },
    { label: "Subject", name: "subjectId", options: subjects, labelKey: "subjectName", valueKey: "subjectId" },
    { label: "Exam Types", name: "examType", options: examTypes, labelKey: "label", valueKey: "label" },
  ];

  return (
    <>
      <TeacherHeader />
      <div className="container mt-5 mb-5">
        <div className="row g-3">
          {dropdowns.map(({ label, name, options, labelKey, valueKey }) => (
            <div className="col-md-3" key={name}>
              <label className="form-label">{label}</label>
              <select className="form-select" name={name} value={selected[name]} onChange={handleInputChange}>
                <option value="">Select {label}</option>
                {options.map((opt) => (
                  <option key={opt[valueKey]} value={opt[valueKey]}>
                    {opt[labelKey]}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <table className="table table-striped table-hover mt-3">
          <thead>
            <tr>
              <th>#</th>
              {/* <th>Roll No</th>
              <th>GR.No.</th> */}
              <th>Exam Seat No</th>
              <th>Name</th>
              {selected.examType !== "Final" ?
                <th style={{ textAlign: "left" }}>Obtained Marks</th> :
                <th colSpan={2} style={{ textAlign: "Center" }}>Obtained Marks</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student, index) => {
              return (
                <tr key={student.studentId}>
                  <td>{index + 1}</td>
                  {/* <td>{student.rollNo}</td>
                  <td>{student.generalRegisterNo}</td> */}
                  <td>{student.examSeatNo}</td>
                  <td>{`${student.firstName} ${student.middleName} ${student.lastName}`}</td>

                  {selected.examType !== "Final" ? (
                    <td>
                      <input type="number" value={student.marksObtained} className="form-control w-50" disabled />
                    </td>
                  ) : (
                    // (examMaxMarks?.finalCategories
                    //   ? examMaxMarks.finalCategories.filter((cat) => cat.maxMarks > 0)
                    //   : Object.keys(student.finalMarks || []).map((k) => ({ type: k, maxMarks: student.examMaxMarks?.[k] ?? 0 })))
                    //   .map((cat) => (
                    (student.examMaxMarks?.finalCategories?.length
                      ? student.examMaxMarks.finalCategories.filter((cat) => cat.maxMarks > 0)
                      : Object.keys(student.finalMarks || []).map((k) => {
                        // Safely read max marks for each Final category
                        const possibleMax =
                          student.examMaxMarks?.[k] ??
                          student.examMaxMarks?.FinalTheory ??
                          student.examMaxMarks?.FinalOral ??
                          student.examMaxMarks?.FinalJournal ??
                          student.examMaxMarks?.FinalProject ??
                          student.examMaxMarks?.FinalPractical ??
                          0;
                        return { type: k, maxMarks: possibleMax };
                      }))
                      .map((cat) => (
                        <td key={cat.type}>
                          <input
                            type="number"
                            value={student.finalMarks?.[cat.type]?.marks ?? 0}
                            className="form-control w-50"
                            disabled={student.isEditAllowedIsEditMarks !== 3}
                            max={cat.maxMarks}
                            placeholder={cat.type.replace("Final", "")}
                            onChange={(e) => {
                              if (!student.finalMarks) student.finalMarks = {};
                              student.finalMarks[cat.type] = {
                                marks: Number(e.target.value),
                                marksFeedingId: student.marksFeedingIds?.[cat.type] || null,
                              };
                            }}
                          />
                        </td>
                      ))
                  )}
                  <td>
                    <button
                      className="btn btn-success"
                      onClick={() => handleUpdateMarks(student)}
                    >
                      Update Marks
                    </button>
                  </td>
                  {/* <td>
                    {student.isEditAllowed === 1 && student.editCount < 2 ? (
                      // Initial or re-locked state → Teacher can raise edit request again (if less than 2 edits done)
                      <button
                        className="btn btn-primary"
                        onClick={() => handleRaiseEditMarksRequest(student.marksFeedingId)}
                      >
                        Raise Edit Request
                      </button>
                    ) : student.isEditAllowed === 2 ? (
                      // Request already raised → waiting for approval
                      <span className="btn btn-warning">Edit Request Raised</span>
                    ) : student.isEditAllowed === 3 ? (
                      // Approved → Teacher can update marks now
                      <button
                        className="btn btn-success"
                        onClick={() => handleUpdateMarks(student)}
                      >
                        Update Marks
                      </button>
                    ) : student.isEditAllowed === 4 && student.editCount < 2 ? (
                      // Marks locked, but teacher can still raise another request (second time)
                      <button
                        className="btn btn-primary"
                        onClick={() => handleRaiseEditMarksRequest(student.marksFeedingId)}
                      >
                        Raise Edit Request
                      </button>
                    ) : (
                      // All edit cycles used up
                      <span className="btn btn-secondary">Marks Locked</span>
                    )}
                  </td> */}

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isModalOpen && selectedStudent && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Update Marks</h5>
                <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                {selected.examType !== "Final" ? (
                  <div className="mb-3">
                    <label>
                      {selectedStudent.marksFeedingExamType} (Max {selectedStudent.maxMarks})
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={marksData[selected.examType] ?? selectedStudent.marksObtained ?? 0}
                      onChange={(e) => handleMarksChange(selected.examType, e.target.value, selectedStudent.maxMarks)}
                    />
                  </div>
                ) : (
                  // (selectedStudent.examMaxMarks?.finalCategories
                  //   ? selectedStudent.examMaxMarks.finalCategories.filter((cat) => cat.maxMarks > 0)
                  //   : Object.keys(selectedStudent.finalMarks || []).map((k) => ({ type: k, maxMarks: selectedStudent.examMaxMarks?.[k] ?? 0 })))
                  //   .map((cat) => (
                  (selectedStudent.examMaxMarks?.finalCategories?.length
                    ? selectedStudent.examMaxMarks.finalCategories.filter((cat) => cat.maxMarks > 0)
                    : Object.keys(selectedStudent.finalMarks || []).map((k) => {
                      // Safely read max marks for each Final category
                      const possibleMax =
                        selectedStudent.examMaxMarks?.[k] ??
                        selectedStudent.examMaxMarks?.FinalTheory ??
                        selectedStudent.examMaxMarks?.FinalOral ??
                        selectedStudent.examMaxMarks?.FinalJournal ??
                        selectedStudent.examMaxMarks?.FinalProject ??
                        selectedStudent.examMaxMarks?.FinalPractical ??
                        0;
                      return { type: k, maxMarks: possibleMax };
                    }))
                    .map((cat) => (
                      <div className="mb-3" key={cat.type}>
                        <label>{cat.type.replace("Final", "")} (Max {cat.maxMarks})</label>
                        <input
                          type="number"
                          className="form-control"
                          value={marksData[cat.type] ?? selectedStudent.finalMarks?.[cat.type]?.marks ?? 0}
                          onChange={(e) => handleMarksChange(cat.type, e.target.value, cat.maxMarks)}
                        />
                      </div>
                    ))
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSubmitMarks}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
      <Footer />
    </>
  );
};

export default ViewMarksFeeding;
